export default `
# Authentication

> Before using the API, you must provide the public source IP address from which requests will be made. This IP must be authorized and registered in our systems beforehand to ensure security and access control. Requests from unregistered IPs will be automatically rejected.

The API authentication flow is token-based for integrations. Send \`X-Auth-Mode: token\`
when opening or refreshing a session so the Auth service returns the JWT and refresh
token in the response body.

## Token response mode

Always include this header in API authentication calls:

\`\`\`text
X-Auth-Mode: token
\`\`\`

With this mode, \`POST /api/auth/session\` returns a [Session](/docs/api/session) object.
For a successful API login, that object contains both tokens:

\`\`\`json
{
  "jwtToken": "<jwt access token>",
  "refreshToken": "<refresh token>",
  "mfaProvider": null
}
\`\`\`

Store both tokens securely. Use \`jwtToken\` to call protected endpoints and use
\`refreshToken\` only to request a new token pair.

## Token lifetime

| Token | Duration | Usage |
|-------|----------|-------|
| \`jwtToken\` | 30 minutes | Access token. Send it as \`Authorization: Bearer <jwtToken>\` in protected API calls. |
| \`refreshToken\` | 40 minutes | Refresh token. Send it to \`PUT /api/auth/session\` to obtain a new \`Session\`. |

Refresh the session before the \`refreshToken\` expires. After that period, the integration
must open a new session with \`POST /api/auth/session\`.

## Log in

\`\`\`endpoint
POST /api/auth/session
\`\`\`

Invalid credentials return HTTP \`401 Unauthorized\` with a plain-text error prefixed
by \`[ERR_AUTH_CREDENTIALS]\`. The same error is used for an unknown user, an incorrect password, or an invalid or expired MFA code.

Request body:

| Field           | Type   | Required | Description                                      |
|-----------------|--------|----------|--------------------------------------------------|
| \`userEmail\`     | string | yes      | User email address.                              |
| \`userPassword\`  | string | yes      | User password.                                   |
| \`realm\`         | string | no       | Realm to bind the session to.                    |

\`\`\`tabs-auth-login
===curl
curl -X POST "https://<your-postamail-domain>/api/auth/session" \\
  -H "Content-Type: application/json" \\
  -H "X-Auth-Mode: token" \\
  -d '{ "userEmail": "you@example.com", "userPassword": "your-password", "realm": "your-realm" }'
===node
const response = await fetch('https://<your-postamail-domain>/api/auth/session', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Auth-Mode': 'token'
  },
  body: JSON.stringify({
    userEmail: 'you@example.com',
    userPassword: 'your-password',
    realm: 'your-realm'
  })
})
const session = await response.json()
===python
import requests

response = requests.post(
  'https://<your-postamail-domain>/api/auth/session',
  headers={'X-Auth-Mode': 'token'},
  json={
    'userEmail': 'you@example.com',
    'userPassword': 'your-password',
    'realm': 'your-realm'
  }
)
response.raise_for_status()
session = response.json()
===php
<?php
$payload = json_encode(array(
  'userEmail' => 'you@example.com',
  'userPassword' => 'your-password',
  'realm' => 'your-realm'
));

$curl = curl_init('https://<your-postamail-domain>/api/auth/session');
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_HTTPHEADER => array(
    'Content-Type: application/json',
    'X-Auth-Mode: token'
  ),
  CURLOPT_POSTFIELDS => $payload
));
$session = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
using System.Net.Http.Json;

using var client = new HttpClient();
using var request = new HttpRequestMessage(HttpMethod.Post,
  "https://<your-postamail-domain>/api/auth/session")
{
  Content = JsonContent.Create(new {
    userEmail = "you@example.com",
    userPassword = "your-password",
    realm = "your-realm"
  })
};
request.Headers.Add("X-Auth-Mode", "token");
using var response = await client.SendAsync(request);
response.EnsureSuccessStatusCode();
var session = await response.Content.ReadFromJsonAsync<Session>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://<your-postamail-domain>/api/auth/session"))
  .header("Content-Type", "application/json")
  .header("X-Auth-Mode", "token")
  .POST(HttpRequest.BodyPublishers.ofString("""
    {"userEmail":"you@example.com","userPassword":"your-password","realm":"your-realm"}
    """))
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

## Authenticated requests

Send the access token in the \`Authorization\` header on every protected API call:

\`\`\`text
Authorization: Bearer <jwtToken>
\`\`\`

Example:

\`\`\`tabs
===curl
curl -X GET "https://<your-postamail-domain>/api/outbox/shipmentlist?page=0&pagesize=25" \\
  -H "Authorization: Bearer <jwtToken>"
===node
const response = await fetch('https://<your-postamail-domain>/api/outbox/shipmentlist?page=0&pagesize=25', {
  headers: { Authorization: 'Bearer ' + jwtToken }
})
const shipments = await response.json()
===python
response = requests.get(
  'https://<your-postamail-domain>/api/outbox/shipmentlist?page=0&pagesize=25',
  headers={'Authorization': 'Bearer ' + jwt_token}
)
shipments = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$curl = curl_init('https://<your-postamail-domain>/api/outbox/shipmentlist?page=0&pagesize=25');
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken)
));
$shipments = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
using var request = new HttpRequestMessage(HttpMethod.Get,
  "https://<your-postamail-domain>/api/outbox/shipmentlist?page=0&pagesize=25");
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
var shipments = await response.Content.ReadFromJsonAsync<object>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://<your-postamail-domain>/api/outbox/shipmentlist?page=0&pagesize=25"))
  .header("Authorization", "Bearer " + jwtToken)
  .GET()
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

## Refresh tokens

\`\`\`endpoint
PUT /api/auth/session
\`\`\`

When \`jwtToken\` expires, refresh the session by sending the current \`refreshToken\` as a
JSON string in the request body. Include \`X-Auth-Mode: token\` so the new token pair is
returned in the response body.

\`\`\`tabs-auth-refresh
===curl
curl -X PUT "https://<your-postamail-domain>/api/auth/session" \\
  -H "Content-Type: application/json" \\
  -H "X-Auth-Mode: token" \\
  -H "Authorization: Bearer <expired-or-current-jwtToken>" \\
  -d '"<refreshToken>"'
===node
const response = await fetch('https://<your-postamail-domain>/api/auth/session', {
  method: 'PUT',
  headers: {
    'Content-Type': 'application/json',
    'X-Auth-Mode': 'token',
    Authorization: 'Bearer ' + jwtToken
  },
  body: JSON.stringify(refreshToken)
})
const refreshedSession = await response.json()
===python
response = requests.put(
  'https://<your-postamail-domain>/api/auth/session',
  headers={
    'Content-Type': 'application/json',
    'X-Auth-Mode': 'token',
    'Authorization': 'Bearer ' + jwt_token
  },
  json=refresh_token
)
response.raise_for_status()
refreshed_session = response.json()
===php
<?php
$jwtToken = '<expired-or-current-jwtToken>';
$refreshToken = '<refreshToken>';

$curl = curl_init('https://<your-postamail-domain>/api/auth/session');
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_CUSTOMREQUEST => 'PUT',
  CURLOPT_HTTPHEADER => array(
    'Content-Type: application/json',
    'X-Auth-Mode: token',
    'Authorization: Bearer ' . $jwtToken
  ),
  CURLOPT_POSTFIELDS => json_encode($refreshToken)
));
$refreshedSession = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
using var request = new HttpRequestMessage(HttpMethod.Put,
  "https://<your-postamail-domain>/api/auth/session")
{
  Content = JsonContent.Create(refreshToken)
};
request.Headers.Add("X-Auth-Mode", "token");
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
response.EnsureSuccessStatusCode();
var refreshedSession = await response.Content.ReadFromJsonAsync<Session>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://<your-postamail-domain>/api/auth/session"))
  .header("Content-Type", "application/json")
  .header("X-Auth-Mode", "token")
  .header("Authorization", "Bearer " + jwtToken)
  .PUT(HttpRequest.BodyPublishers.ofString('"' + refreshToken + '"'))
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

The refresh response has the same shape as login:

\`\`\`json
{
  "jwtToken": "<new jwt access token>",
  "refreshToken": "<new refresh token>",
  "mfaProvider": null
}
\`\`\`

Replace both stored tokens after every successful refresh. Refresh tokens are rotated;
once a new \`refreshToken\` is returned, the previous value should no longer be used.

## Expired or invalid refresh token

If the refresh token is missing, invalid, expired, or has already been rotated, the refresh
call returns no usable session. Discard both tokens and ask the user or integration to log
in again.

`



// WEBPACK FOOTER //
// ./src/components/docs/api/content/authentication.js